import React, { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import { diagnosticsSftpListDirectory } from '_services/ApiService';
import ErrorBoundary from 'components/Error/ErrorBoundary';
import { createJobConfigHooks } from './hooks';
import { JobDetailsCard } from './components/JobDetailsCard';
import { NotificationsCard } from './components/NotificationsCard';
import { SeedTalentScheduleCard } from './components/SeedTalentScheduleCard';
import { useModalContext } from '../ModalContext';

const isConcealedSecret = (value) => value !== null && typeof value === 'object';

const DEFAULT_CONFIG = {
  companyName: 'TerrAscend',
  kronos: {
    type: '1password_item',
    '1password_item_path': 'Client credentials/UKG - EnjoyWurkAdmin - apiuser',
  },
  sftp: {
    host: 'sftp.charlotte.enjoywurk.com',
    port: 22,
    username: '',
    password: '',
  },
  sourceDirectory: '/inbound',
  archiveDirectory: '/archive',
  failureDirectory: '/failed',
  importDetails: {
    type_id: '100',
  },
  emailConfig: {
    emailContacts: [],
    sendSuccessEmails: 'true',
  },
  pgp: {},
};

const { useJobGeneralConfig, useJobConfig } = createJobConfigHooks({
  partner: 'TerrAscend',
  defaultConfig: DEFAULT_CONFIG,
});

const SavedSecretField = ({ label, secret, onChange }) => {
  const saved = isConcealedSecret(secret);
  const [replacing, setReplacing] = useState(false);
  if (saved && !replacing) {
    return (
      <TextField
        fullWidth
        helperText="Saved securely. It is not shown again."
        label={label}
        placeholder="••••••••"
        type="password"
        value=""
        variant="standard"
        InputProps={{
          readOnly: true,
          endAdornment: (
            <Button onClick={() => setReplacing(true)} size="small">
              Replace
            </Button>
          ),
        }}
      />
    );
  }
  return (
    <TextField
      autoComplete="new-password"
      fullWidth
      helperText="Stored encrypted when the job is saved, and not shown again."
      label={label}
      onChange={(e) => onChange(e.target.value)}
      required
      type="password"
      value={typeof secret === 'string' ? secret : ''}
      variant="standard"
    />
  );
};

const ClientCard = () => {
  const [companyName, setCompanyName] = useJobConfig(['companyName']);
  const [companyId, setCompanyId] = useJobConfig(['importDetails', 'company_id']);
  const [typeId, setTypeId] = useJobConfig(['importDetails', 'type_id']);
  return (
    <Card>
      <CardHeader
        subheader="TerrAscend employee records load into UKG import type 100"
        title="Import"
      />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item md={4} xs={12}>
            <TextField
              fullWidth
              label="Company short name"
              onChange={(e) => setCompanyName(e.target.value)}
              required
              value={companyName || ''}
              variant="standard"
            />
          </Grid>
          <Grid item md={4} xs={12}>
            <TextField
              fullWidth
              label="UKG company ID"
              onChange={(e) => {
                const raw = e.target.value;
                setCompanyId(raw === '' ? undefined : Number(raw));
              }}
              required
              type="number"
              value={companyId ?? ''}
              variant="standard"
            />
          </Grid>
          <Grid item md={4} xs={12}>
            <TextField
              fullWidth
              helperText="TerrAscend uses 100"
              label="Import type ID"
              onChange={(e) => setTypeId(e.target.value)}
              required
              value={typeId || '100'}
              variant="standard"
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const SftpCard = () => {
  const { formState, setFormState } = useModalContext();
  const [sftp] = useJobConfig(['sftp']);
  const [sourceDirectory, setSourceDirectory] = useJobConfig(['sourceDirectory']);
  const [archiveDirectory, setArchiveDirectory] = useJobConfig(['archiveDirectory']);
  const [failureDirectory, setFailureDirectory] = useJobConfig(['failureDirectory']);
  const [, setVerified] = useJobGeneralConfig(['sftpConnectionVerified']);
  const connection = sftp || {};
  const [testStatus, setTestStatus] = useState('');

  const update = (next) => {
    const updated = JSON.parse(JSON.stringify(formState));
    const raw = updated.values.config;
    const config = !raw || raw === '{ }' ? JSON.parse(JSON.stringify(DEFAULT_CONFIG)) : JSON.parse(raw);
    config.sftp = next;
    updated.values.config = JSON.stringify(config, null, 2);
    updated.values.sftpConnectionVerified = false;
    setFormState(updated);
    setTestStatus('');
  };

  const testConnection = async () => {
    setTestStatus('testing');
    try {
      const result = await diagnosticsSftpListDirectory({
        credentials: connection,
        directory: sourceDirectory || '/',
      });
      if (result?.type === 'error') throw new Error(result.message || 'SFTP connection failed');
      setVerified(true);
      setTestStatus('ok');
    } catch (error) {
      setVerified(false);
      setTestStatus(error.message || 'SFTP connection failed');
    }
  };

  return (
    <Card>
      <CardHeader
        subheader="Wurk hosts this SFTP. Dayforce uploads the encrypted file here."
        title="SFTP"
      />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item md={6} xs={12}>
            <TextField
              fullWidth
              label="Host"
              onChange={(e) => update({ ...connection, host: e.target.value })}
              required
              value={connection.host || ''}
              variant="standard"
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <TextField
              fullWidth
              label="Port"
              onChange={(e) => update({
                ...connection,
                port: e.target.value === '' ? '' : Number(e.target.value),
              })}
              required
              type="number"
              value={connection.port ?? ''}
              variant="standard"
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <TextField
              fullWidth
              label="Username"
              onChange={(e) => update({ ...connection, username: e.target.value })}
              required
              value={connection.username || ''}
              variant="standard"
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <SavedSecretField
              label="SFTP password"
              onChange={(password) => update({ ...connection, password })}
              secret={connection.password ?? ''}
            />
          </Grid>
          <Grid item md={4} xs={12}>
            <TextField
              fullWidth
              label="Upload directory"
              onChange={(e) => setSourceDirectory(e.target.value)}
              required
              value={sourceDirectory || ''}
              variant="standard"
            />
          </Grid>
          <Grid item md={4} xs={12}>
            <TextField
              fullWidth
              label="Archive directory"
              onChange={(e) => setArchiveDirectory(e.target.value)}
              required
              value={archiveDirectory || ''}
              variant="standard"
            />
          </Grid>
          <Grid item md={4} xs={12}>
            <TextField
              fullWidth
              label="Failure directory"
              onChange={(e) => setFailureDirectory(e.target.value)}
              value={failureDirectory || ''}
              variant="standard"
            />
          </Grid>
        </Grid>
        <Button color="primary" onClick={testConnection} sx={{ marginTop: 2 }} variant="contained">
          Test connection
        </Button>
        {testStatus === 'ok' && (
          <Alert severity="success" sx={{ marginTop: 2 }}>SFTP connection succeeded.</Alert>
        )}
        {testStatus && testStatus !== 'ok' && testStatus !== 'testing' && (
          <Alert severity="error" sx={{ marginTop: 2 }}>{testStatus}</Alert>
        )}
      </CardContent>
    </Card>
  );
};

const PgpCard = () => {
  const [pgp, setPgp] = useJobConfig(['pgp']);
  const current = pgp || {};
  return (
    <Card>
      <CardHeader
        subheader="Dayforce encrypts the file with the public key. The job decrypts it with the private key."
        title="PGP"
      />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              helperText="Share this with Dayforce. It is not a secret."
              label="PGP public key"
              multiline
              onChange={(e) => setPgp({ ...current, publicKey: e.target.value })}
              value={typeof current.publicKey === 'string' ? current.publicKey : ''}
              variant="standard"
            />
          </Grid>
          <Grid item xs={12}>
            <SavedSecretField
              label="PGP private key"
              onChange={(privateKey) => setPgp({ ...current, privateKey })}
              secret={current.privateKey ?? ''}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const KronosCard = () => {
  const [kronos, setKronos] = useJobConfig(['kronos']);
  const auth = kronos || {};
  const mode = auth.type === '1password_item' ? 'onepassword' : 'direct';
  return (
    <Card>
      <CardHeader subheader="UKG credentials used to load the employee file" title="Kronos" />
      <CardContent>
        <FormControl sx={{ marginBottom: '16px' }}>
          <RadioGroup
            onChange={(e) => {
              if (e.target.value === 'onepassword') {
                setKronos({ type: '1password_item', '1password_item_path': '' });
              } else {
                setKronos({
                  companyName: '',
                  username: '',
                  apiKey: '',
                  password: '',
                  baseUrl: 'https://secure.saashr.com',
                });
              }
            }}
            row
            value={mode}
          >
            <FormControlLabel control={<Radio />} label="Reference a 1Password item" value="onepassword" />
            <FormControlLabel control={<Radio />} label="Enter credentials directly" value="direct" />
          </RadioGroup>
        </FormControl>
        {mode === 'onepassword' ? (
          <TextField
            fullWidth
            label="1Password item path"
            onChange={(e) => setKronos({ ...auth, '1password_item_path': e.target.value })}
            required
            value={auth['1password_item_path'] || ''}
            variant="standard"
          />
        ) : (
          <Grid container spacing={2}>
            <Grid item md={6} xs={12}>
              <TextField fullWidth label="Kronos company name" onChange={(e) => setKronos({ ...auth, companyName: e.target.value })} required value={auth.companyName || ''} variant="standard" />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField fullWidth label="Kronos username" onChange={(e) => setKronos({ ...auth, username: e.target.value })} required value={auth.username || ''} variant="standard" />
            </Grid>
            <Grid item md={6} xs={12}>
              <SavedSecretField label="Kronos API key" onChange={(apiKey) => setKronos({ ...auth, apiKey })} secret={auth.apiKey ?? ''} />
            </Grid>
            <Grid item md={6} xs={12}>
              <SavedSecretField label="Kronos password" onChange={(password) => setKronos({ ...auth, password })} secret={auth.password ?? ''} />
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

export function validateDayforceEmployeeImport(values) {
  const errors = [];
  if (!values?.jobName?.trim()) errors.push('Job name is required');
  let config = {};
  try {
    config = !values?.config || values.config === '{ }' ? DEFAULT_CONFIG : JSON.parse(values.config);
  } catch (error) {
    return ['Configuration is not valid JSON'];
  }
  if (!config.companyName?.trim()) errors.push('Company short name is required');
  if (!String(config.importDetails?.type_id || '').trim()) errors.push('Import type ID is required');
  if (typeof config.importDetails?.company_id !== 'number' || Number.isNaN(config.importDetails.company_id)) {
    errors.push('UKG company ID is required');
  }
  if (!config.sftp?.host?.trim()) errors.push('SFTP host is required');
  if (!config.sftp?.username?.trim()) errors.push('SFTP username is required');
  if (!config.sourceDirectory?.trim()) errors.push('Upload directory is required');
  const password = config.sftp?.password;
  const hasPassword = (typeof password === 'string' && password.trim()) || isConcealedSecret(password);
  if (config.sftp?.type !== '1password_item' && !hasPassword) errors.push('SFTP password is required');
  const privateKey = config.pgp?.privateKey;
  const hasKey = (typeof privateKey === 'string' && privateKey.trim())
    || isConcealedSecret(privateKey)
    || (privateKey && typeof privateKey.key === 'string');
  if (!hasKey) errors.push('PGP private key is required');
  if (values?.status === 'enabled' && !values?.sftpConnectionVerified) {
    errors.push('Test the SFTP connection before activating the feed');
  }
  return errors;
}

export const DayforceEmployeeImportForm = () => (
  <ErrorBoundary context="Dayforce employee import form">
    <Grid container spacing={2}>
      <Grid item xs={12}><JobDetailsCard useJobGeneralConfig={useJobGeneralConfig} /></Grid>
      <Grid item xs={12}><ClientCard /></Grid>
      <Grid item xs={12}><KronosCard /></Grid>
      <Grid item xs={12}><SftpCard /></Grid>
      <Grid item xs={12}><PgpCard /></Grid>
      <Grid item xs={12}><NotificationsCard useJobConfig={useJobConfig} /></Grid>
      <Grid item xs={12}><SeedTalentScheduleCard useJobGeneralConfig={useJobGeneralConfig} /></Grid>
    </Grid>
  </ErrorBoundary>
);

export default DayforceEmployeeImportForm;
